import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import { silentHttpContext } from '../http/http-loading.context';
import {
  ChatMessage,
  ConversationDetail,
  ConversationListItem,
  SendMessageResult,
  UnreadCount,
  VisitorEngageResult
} from '../models/chat.model';

export const VISITOR_CHAT_STORAGE = {
  visitorKey: 'ecommerce.chat.visitorKey',
  conversationId: 'ecommerce.chat.conversationId'
} as const;

@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private readonly http = inject(HttpClient);

  visitorEngage(body: {
    visitorKey: string;
    greetingBody?: string;
  }): Observable<VisitorEngageResult> {
    return this.http.post<VisitorEngageResult>(`${API_BASE_URL}/chat/engage`, body);
  }

  visitorAbandon(body: {
    visitorKey: string;
    conversationId?: string | null;
  }): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/chat/abandon`, body);
  }

  visitorSend(body: {
    visitorKey: string;
    body: string;
    conversationId?: string | null;
    clientMessageId?: string;
  }): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${API_BASE_URL}/chat/messages`, body);
  }

  visitorMessages(
    conversationId: string,
    visitorKey: string,
    options?: { silent?: boolean; afterMessageId?: string | null }
  ): Observable<ChatMessage[]> {
    const params: Record<string, string> = { visitorKey };
    if (options?.afterMessageId) {
      params['afterMessageId'] = options.afterMessageId;
    }
    return this.http.get<ChatMessage[]>(
      `${API_BASE_URL}/chat/conversations/${conversationId}/messages`,
      {
        params,
        ...(options?.silent ? { context: silentHttpContext } : {})
      }
    );
  }

  adminList(silent = false): Observable<ConversationListItem[]> {
    return this.http.get<ConversationListItem[]>(`${API_BASE_URL}/chat/conversations`, {
      ...(silent ? { context: silentHttpContext } : {})
    });
  }

  adminGet(
    id: string,
    options?: { silent?: boolean; afterMessageId?: string | null }
  ): Observable<ConversationDetail> {
    const params: Record<string, string> = {};
    if (options?.afterMessageId) {
      params['afterMessageId'] = options.afterMessageId;
    }
    return this.http.get<ConversationDetail>(`${API_BASE_URL}/chat/conversations/${id}`, {
      params,
      ...(options?.silent ? { context: silentHttpContext } : {})
    });
  }

  adminReply(id: string, body: string, clientMessageId?: string): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${API_BASE_URL}/chat/conversations/${id}/messages`, {
      body,
      clientMessageId
    });
  }

  adminMarkRead(id: string): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/chat/conversations/${id}/read`, {});
  }

  unreadCount(silent = false): Observable<UnreadCount> {
    return this.http.get<UnreadCount>(`${API_BASE_URL}/chat/unread-count`, {
      ...(silent ? { context: silentHttpContext } : {})
    });
  }

  vapidPublicKey(): Observable<{ publicKey: string }> {
    return this.http.get<{ publicKey: string }>(`${API_BASE_URL}/push/vapid-public-key`);
  }

  subscribePush(payload: {
    endpoint: string;
    p256dh: string;
    auth: string;
  }): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/push/subscriptions`, payload);
  }
}

export function chatHubUrl(): string {
  const api = API_BASE_URL.replace(/\/+$/, '');
  if (api.endsWith('/api')) {
    return `${api}/hubs/chat`;
  }
  return `${api}/api/hubs/chat`;
}
